import { useEffect, useState } from 'react';
import Editor, { OnMount } from '@monaco-editor/react';

interface CodeEditorProps {
  code: string;
  onChange: (code: string) => void;
}

export default function CodeEditor({ code, onChange }: CodeEditorProps) {
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    // Watch for theme changes
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  const handleEditorDidMount: OnMount = (_editor, monaco) => {
    // Define custom dark theme matching Tailwind dark colors
    monaco.editor.defineTheme('tailwind-dark', {
      base: 'vs-dark',
      inherit: true,
      rules: [],
      colors: {
        'editor.background': '#1f2937', // Tailwind gray-900
        'editor.lineHighlightBackground': '#111827', // Tailwind gray-800
      }
    });

    // Define custom light theme matching Tailwind light colors
    monaco.editor.defineTheme('tailwind-light', {
      base: 'vs',
      inherit: true,
      rules: [],
      colors: {
        'editor.background': '#ffffff', // White
        'editor.lineHighlightBackground': '#f9fafb', // Tailwind gray-50
      }
    });

    // Set initial theme
    monaco.editor.setTheme(isDarkMode ? 'tailwind-dark' : 'tailwind-light');
  };

  return (
    <div className="h-full">
      <div className="w-full h-full rounded-lg shadow-xl overflow-hidden border border-gray-300 dark:border-gray-700">
        <Editor
          height="100%"
          defaultLanguage="html"
          value={code}
          onChange={(value) => onChange(value || '')}
          onMount={handleEditorDidMount}
          theme={isDarkMode ? 'tailwind-dark' : 'tailwind-light'}
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            automaticLayout: true,
            tabSize: 2,
          }}
        />
      </div>
    </div>
  );
}

